/**
 * Qualification des cellules de la matrice de comparaison — chunk 9.AB.1.
 *
 * ## Le problème mesuré
 *
 * Le smoke 9.AA.1b a produit 124 cellules sur 43 lignes. Une cellule vide ne disait
 * pas ce qu'elle voulait dire : Verchères n'a aucune valeur pour
 * `ic_long_terme.percentage` — l'assureur n'offre pas l'invalidité longue durée, ou
 * on a raté l'extraction ? L'écart est décisif pour une recommandation, et il
 * s'affichait comme une case blanche indistinguable d'un échec.
 *
 * Deuxième constat : 45 des 124 cellules portaient `needs_review`, dont **28
 * simplement parce que la valeur était nulle avec une confiance de 0**. Le drapeau
 * confondait « absent » et « douteux ». Un signal posé sur 36 % des cellules cesse
 * d'être un signal.
 *
 * ## Cinq états distincts, décision PO
 *
 * Et un ordre d'évaluation qui n'est pas arbitraire :
 *
 *   1. `extraction_failed` domine tout. Si la passe n'a pas abouti, on ne sait rien
 *      de cette cellule — ni qu'elle est vide, ni qu'elle est douteuse ;
 *   2. sans valeur → `not_offered` s'il existe une **preuve négative explicite**,
 *      sinon `not_detected`. Jamais l'inverse : l'absence seule ne prouve rien ;
 *   3. avec valeur → `needs_review` si un doute réel subsiste, sinon `value`.
 *
 * ## `needs_review` n'était pas cassé, il était MAL PORTÉ
 *
 * Découverte utile : sur une cellule **qui porte une valeur**, le `needs_review`
 * existant est informatif — il vient d'une confiance basse, d'une citation absente
 * ou d'un désaccord entre modèles. Le bruit ne venait que des cellules **nulles**,
 * où il est mécanique.
 *
 * On n'a donc pas besoin d'inventer une heuristique de doute : il suffit de cesser
 * d'appliquer le drapeau là où il ne veut rien dire. `needs_review` reste par
 * ailleurs intact sur le wire pour ses consommateurs existants — décision PO, la
 * dépréciation éventuelle sera décidée séparément.
 */

import { guaranteeFamilyOf } from "./field-registry.js";

/** Les six significations. Distinctes, et jamais confondues. */
export type CellStatus =
  /** Une valeur exploitable a été extraite. */
  | "value"
  /** La garantie est explicitement indiquée comme non offerte. */
  | "not_offered"
  /**
   * Aucune valeur détectée, et **on ne sait pas si le document en contient une**.
   *
   * Distinct de `not_provided` : ici, aucune lecture n'a rapporté ce champ. Le
   * silence vient de NOUS, pas du document.
   */
  | "not_detected"
  /**
   * Le document ne fournit pas cette information — 9.AM.1.
   *
   * Condition STRICTE : au moins une lecture a rapporté ce champ, **toutes** ont
   * rendu une valeur nulle, et aucune preuve négative n'a été trouvée. Autrement
   * dit : le modèle a regardé, et la proposition est muette.
   *
   * Ce n'est **ni** `not_offered` — personne n'a écrit que la garantie est exclue —
   * **ni** un zéro, **ni** un défaut d'extraction. C'est un silence du document, et
   * le dire vaut mieux que demander une vérification qui ne trouvera rien.
   *
   * Un seul candidat non nul suffit à interdire cet état : la valeur existe alors
   * quelque part, et c'est un autre problème.
   */
  | "not_provided"
  /** Une valeur existe, mais un doute réel demande une vérification humaine. */
  | "needs_review"
  /**
   * La passe n'a pas abouti, ou n'a jamais atteint de réconciliation exploitable.
   *
   * ⚠️ **Ce n'est PAS le mécanisme principal de signalement d'un échec.** Une passe
   * qui échoue AVANT de produire des champs n'insère rien dans
   * `app.extracted_fields` : il n'existe alors aucune cellule à qualifier. Mesuré
   * sur staging — les 9 passes `failed` ont toutes zéro champ.
   *
   * Ce statut ne couvre donc, en pratique, qu'un cas : une cellule qui **existe
   * déjà** grâce à un résultat antérieur, et dont une relance a échoué. L'échec de
   * première exécution est signalé par `failed_passes` au niveau de la soumission
   * (décision PO option B) — voir `ComparisonSubmissionSchema`.
   */
  | "extraction_failed";

/** Pourquoi cet état — toujours renseigné, pour que l'UI n'ait rien à deviner. */
export type CellStatusReason =
  | "usable_value"
  | "explicit_negative_mention"
  | "no_value_no_evidence"
  /** 9.AM.1 — toutes les lectures ont rapporté ce champ vide. */
  | "reported_empty_by_all"
  | "low_confidence_or_missing_citation"
  | "unrecognized_categorical_value"
  | "pass_failed";

/** Provenance d'une preuve négative. Sans elle, la preuve n'est pas retenue. */
export type CellEvidence = {
  /** Champ FRÈRE de la même famille qui porte la mention. */
  field_key: string;
  /** Famille de garantie qui rattache la preuve à la cellule. */
  guarantee_family: string;
  /** Mention telle qu'extraite — jamais reformulée. */
  mention: string;
  citation_page: number | null;
  /** Vrai si une citation textuelle accompagne la mention. */
  has_citation_quote: boolean;
  /**
   * Vrai quand la provenance retenue est une CORRECTION HUMAINE (9.AH.1).
   *
   * Une correction humaine arrive sans page ni citation : sans ce drapeau, la
   * charge annonçait `citation_page: null, has_citation_quote: false` et la preuve
   * négative semblait ne reposer sur rien. Elle repose sur la décision d'un
   * courtier, ce qui est au moins aussi solide — mais il faut le DIRE.
   */
  human_override: boolean;
};

export type QualifiedCell = {
  cell_status: CellStatus;
  status_reason: CellStatusReason;
  evidence: CellEvidence | null;
};

// -----------------------------------------------------------------------------
// Preuve négative
// -----------------------------------------------------------------------------

/**
 * Normalise une valeur textuelle pour la reconnaissance de mention : minuscules,
 * accents retirés, espaces réduits. Même approche que `normalizeSectionCode`
 * (9.AA.1) — les documents mélangent « NON OFFERTE », « non offert » et
 * « Non Offerte ».
 */
export function normalizeMention(raw: string): string {
  return (
    raw
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      // Apostrophes typographiques → ASCII. Remarque P3 Codex, fondée : les PDF
      // français écrivent « n’est pas offerte » avec U+2019, et les motifs ne
      // reconnaissaient que l'apostrophe droite. Une mention explicite retombait
      // donc en `not_detected` alors que le document est parfaitement clair.
      .replace(/[’‘‛`´]/g, "'")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/**
 * Formulations qui signifient « cette garantie n'est pas offerte ».
 *
 * Volontairement courtes et sans ambiguïté. On ne cherche pas à couvrir toutes les
 * tournures possibles : rater une preuve donne `not_detected`, ce qui est
 * conservateur et honnête. En inventer une donnerait `not_offered` à tort, et un
 * conseiller écarterait une soumission sur une erreur de lecture.
 */
const MENTIONS_NEGATIVES: readonly RegExp[] = [
  /\bnon offert(e|s|es)?\b/,
  /\bnon couvert(e|s|es)?\b/,
  /\bn'est pas offert(e)?\b/,
  /\bn'est pas couvert(e)?\b/,
  /\bpas de couverture\b/,
  /\baucune couverture\b/,
  /\bsans couverture\b/,
  /\bnot offered\b/,
  /\bnot covered\b/,
  /\bno coverage\b/,
];

/**
 * Longueur maximale d'une mention retenue comme preuve, après normalisation.
 *
 * Une preuve négative est une **mention**, pas une phrase. `"NON OFFERTE"` en est
 * une ; `"Invalidité de longue durée non offerte ; maternité hors régime public"`
 * énumère plusieurs choses et ne qualifie pas un champ précis. Ce plafond est une
 * seconde barrière : la première, décisive, est le rattachement à la famille.
 */
const LONGUEUR_MAX_MENTION = 40;

/**
 * Une valeur constitue-t-elle une mention négative exploitable ?
 *
 * Trois exigences, chacune fermant un faux positif observé ou plausible :
 *
 *   1. **une chaîne** — un nombre est un niveau de couverture, pas une absence ;
 *   2. **aucun chiffre** — `"0 % (non couvert)"` décrit le taux d'UN champ, pas
 *      l'absence de la garantie entière. C'est le piège le plus vicieux : le
 *      document de Verchères contient littéralement cette formulation pour le
 *      dentaire majeur, alors que la famille `dentaire` est bel et bien offerte ;
 *   3. **courte** — cf. `LONGUEUR_MAX_MENTION`.
 */
export function isNegativeMention(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const n = normalizeMention(value);
  if (n.length === 0 || n.length > LONGUEUR_MAX_MENTION) return false;
  if (/\d/.test(n)) return false;
  return MENTIONS_NEGATIVES.some((r) => r.test(n));
}

// -----------------------------------------------------------------------------
// Échelle ordonnée de la chambre d'hôpital
// -----------------------------------------------------------------------------

/**
 * Niveau d'hébergement, du moins au plus couvrant — ordre validé PO :
 * `publique < semi-privée < privée`.
 *
 * Présenté comme un **niveau de couverture d'hébergement**, et rien de plus : la
 * décision PO interdit d'en faire un score global de l'offre dans ce chunk.
 */
export type HospitalRoomLevel = 1 | 2 | 3;

export const HOSPITAL_ROOM_FIELD = "amc.hospital_room";

/**
 * Reconnaît un niveau d'hébergement, ou `null`.
 *
 * `null` n'est pas un échec silencieux : l'appelant en fait un `needs_review` et la
 * valeur reste affichée **en texte**. Décision PO — « les valeurs inconnues restent
 * en texte et passent en vérification plutôt que d'être classées arbitrairement ».
 *
 * Deux pièges traités explicitement :
 *
 *   - **« semi » d'abord.** `"semi-privée"` contient `"privee"` après
 *     normalisation. Tester le privé en premier classerait tous les semi-privés
 *     comme privés — une surévaluation systématique de la couverture ;
 *   - **valeurs combinées.** `"semi-privée ou privée"` mentionne deux niveaux : on
 *     ne choisit pas à la place du conseiller, on renvoie `null`.
 */
export function hospitalRoomLevel(value: unknown): HospitalRoomLevel | null {
  if (typeof value !== "string") return null;
  const n = normalizeMention(value);
  if (n.length === 0) return null;

  // On RETIRE d'abord les occurrences de « semi-privé », puis on cherche un privé
  // résiduel. Rendre simplement `privee` exclusif de `semi` réglait le problème de
  // sous-chaîne mais en créait un autre : `"semi-privée ou privée"` mentionne les
  // DEUX niveaux et serait passé pour un semi-privé net. C'est un test de ce fichier
  // qui l'a attrapé.
  const SEMI = /\bsemi[- ]?privee?\b|\bsemi[- ]?private\b/g;
  const sansSemi = n.replace(SEMI, " ");
  const semi = sansSemi !== n;
  const privee = /\bprivee?\b/.test(sansSemi) || /\bprivate\b/.test(sansSemi);
  const publique = /\bpublique?\b/.test(n) || /\bpublic\b/.test(n) || /\bward\b/.test(n);

  // Deux niveaux nommés dans la même valeur → ambiguë, on ne tranche pas.
  const nb = [semi, privee, publique].filter(Boolean).length;
  if (nb !== 1) return null;

  if (publique) return 1;
  if (semi) return 2;
  return 3;
}

// -----------------------------------------------------------------------------
// Qualification
// -----------------------------------------------------------------------------

/** Ce que l'appelant sait d'une cellule et de ses frères de famille. */
export type CellInput = {
  fieldKey: string;
  value: unknown;
  /** `needs_review` tel que posé par la consolidation. */
  needsReview: boolean;
  /** Statut de l'extraction qui a produit la cellule. */
  extractionStatus: "pending" | "running" | "done" | "failed" | null;
  /**
   * Toutes les lectures ont-elles RAPPORTÉ ce champ, et toutes vides ? — 9.AM.1.
   *
   * `true` exige les deux : au moins un candidat existe, et **aucun** ne porte de
   * valeur. C'est ce qui distingue « le document est muet » de « personne n'a
   * regardé ». `undefined` quand l'appelant ne sait pas — et l'ignorance retombe
   * alors sur `not_detected`, jamais sur une affirmation.
   */
  reportedEmptyByAll?: boolean;
  /**
   * Frères de la MÊME soumission : `field_key` → valeur et provenance. C'est là, et
   * seulement là, qu'une preuve négative peut être trouvée.
   */
  siblings: ReadonlyMap<
    string,
    {
      value: unknown;
      citationPage: number | null;
      hasCitationQuote: boolean;
      /**
       * Le champ a-t-il été corrigé par un humain (`override_by` non nul) ?
       *
       * Une correction humaine tient lieu de provenance : le flux d'édition
       * n'envoie pas les `citation_*` et le `PATCH` les efface, donc une valeur
       * corrigée en « NON OFFERTE » arriverait sans page ni citation.
       */
      isHumanOverride?: boolean;
    }
  >;
};

/**
 * Cherche une preuve négative rattachable à cette cellule.
 *
 * Le rattachement passe **exclusivement** par la famille déclarée au contrat
 * (`guaranteeFamilyOf`). Un champ sans famille ne peut ni recevoir ni fournir de
 * preuve — c'est ce qui empêche `exclusions` de qualifier `ic_long_terme.*`, alors
 * que sa mention est pourtant vraie. La décision PO l'interdit, et cette
 * interdiction est structurelle ici, pas conditionnelle.
 */
export function findNegativeEvidence(input: CellInput): CellEvidence | null {
  const famille = guaranteeFamilyOf(input.fieldKey);
  if (famille === null) return null;

  // La cellule peut porter elle-même la mention (`ic_long_terme.definition`).
  const candidats = [...input.siblings.entries()].filter(
    ([cle]) => guaranteeFamilyOf(cle) === famille,
  );

  // ── UNE FAMILLE QUI PORTE UNE VALEUR POSITIVE EST MANIFESTEMENT OFFERTE ──
  //
  // Remarque P2 Codex, fondée — et le défaut le plus dangereux trouvé sur ce chunk.
  //
  // Sans cette garde, une exclusion de SOUS-garantie contaminait la famille entière.
  // `dentaire.ortho_coinsurance = "Non couvert"` est une exclusion étroite et
  // banale — l'orthodontie n'est presque jamais couverte — et elle aurait marqué
  // TOUS les champs nuls de la famille `dentaire` en `not_offered`, y compris
  // `dentaire.max_annual`. La matrice aurait affirmé que le dentaire n'est pas offert
  // alors qu'il l'est, et un conseiller aurait pu écarter une soumission sur cette
  // base.
  //
  // Mes trois barrières (chaîne, sans chiffre, courte) filtrent ce qui compte comme
  // MENTION ; aucune ne contraignait QUEL frère peut la porter. C'est ce trou-là.
  //
  // Le critère est factuel, pas interprétatif : si un frère de la famille porte une
  // valeur exploitable qui n'est pas elle-même une mention négative, la garantie
  // existe. Un `0` compte comme valeur — « 0 % » est un niveau de couverture.
  //
  // Le cas réel continue de passer : chez Verchères, `ic_long_terme` est entièrement
  // vide hors la mention, donc rien ne bloque.
  const familleAUneValeurPositive = candidats.some(
    ([, frere]) =>
      frere.value !== null && frere.value !== undefined && !isNegativeMention(frere.value),
  );
  if (familleAUneValeurPositive) return null;

  for (const [cle, frere] of candidats) {
    if (!isNegativeMention(frere.value)) continue;
    // Provenance exigée par la décision PO. Sans citation ni page, la mention n'est
    // pas rattachable au document et ne vaut pas preuve.
    //
    // Exception : une CORRECTION HUMAINE. Remarque P2 Codex, fondée —
    // `ExtractedFieldEditor` n'envoie pas les `citation_*` et le `PATCH` les efface,
    // donc une valeur corrigée par un courtier en « NON OFFERTE » arrivait ici sans
    // page ni citation et se voyait refusée. Une décision humaine vaut au moins
    // autant qu'une citation de modèle : elle EST la provenance.
    const aUneProvenance =
      frere.citationPage !== null || frere.hasCitationQuote || frere.isHumanOverride === true;
    if (!aUneProvenance) continue;
    return {
      field_key: cle,
      guarantee_family: famille,
      mention: String(frere.value),
      citation_page: frere.citationPage,
      has_citation_quote: frere.hasCitationQuote,
      // 9.AH.1 — DIRE laquelle des trois provenances a été retenue.
      //
      // Une correction humaine arrive sans page ni citation. L'evidence annonçait
      // donc `citation_page: null, has_citation_quote: false` : sur le wire, une
      // garantie déclarée non offerte semblait ne reposer sur RIEN, alors qu'un
      // courtier l'avait décidée. La règle acceptait la provenance ; la charge ne
      // la rapportait pas, donc personne n'aurait pu justifier la preuve négative.
      //
      // Additif : la règle de qualification est inchangée.
      human_override: frere.isHumanOverride === true,
    };
  }
  return null;
}

/**
 * Qualifie une cellule. Fonction pure : aucune I/O, entièrement testable.
 *
 * Le zéro explicite est traité comme une valeur, pas comme une absence — c'est la
 * première exigence de test de la décision PO, et le smoke l'a montré nécessaire :
 * `amc.deductible = 0` chez Verchères veut dire « aucune franchise », un argument de
 * vente. Le confondre avec un vide effacerait une information commerciale.
 */
export function qualifyCell(input: CellInput): QualifiedCell {
  // 1. L'échec d'extraction domine : on ne sait rien de cette cellule.
  if (input.extractionStatus === "failed") {
    return { cell_status: "extraction_failed", status_reason: "pass_failed", evidence: null };
  }
  const aUneValeur = input.value !== null && input.value !== undefined;

  // Le bilan de réconciliation ne qualifie RIEN — et ce n'est pas un oubli.
  //
  // La migration 0065 a ajouté `reconciliation` sans reprise de l'existant : toute
  // extraction antérieure est `done` avec `reconciliation IS NULL` alors qu'elle a
  // parfaitement abouti. J'en avais tiré une garde partielle — n'alarmer que sur une
  // cellule VIDE — en la justifiant par une mesure qui annonçait « 0 ligne
  // concernée ». Cette mesure était fausse : elle testait `value IS NOT NULL` en SQL,
  // ce qui compte le `jsonb 'null'` comme une valeur, alors que `pg` le rend à
  // JavaScript comme `null`. Au sens du code, **16 cellules** sur 294 tombaient dans
  // la branche, toutes créées entre avril et août, toutes légitimes.
  //
  // Le fond, une fois la mesure refaite : `done` + bilan NULL ne distingue pas une
  // exécution interrompue d'une extraction d'avant 0065. Depuis 9.AA.1a le bilan
  // s'écrit dans la MÊME transaction que la consolidation — des champs consolidés
  // sans bilan ne peuvent donc venir que de l'héritage. Le signal n'a aucun pouvoir
  // discriminant, et la décision PO est explicite : l'historique pré-0065 se traite
  // « comme legacy/inconnu, sans alarme ». Une cellule vide retombe donc sur
  // `not_detected`, qui invite à confirmer au document sans rien affirmer.
  // 2. Sans valeur : preuve négative, ou aveu d'ignorance. Jamais l'inverse.
  if (!aUneValeur) {
    const preuve = findNegativeEvidence(input);
    if (preuve) {
      return {
        cell_status: "not_offered",
        status_reason: "explicit_negative_mention",
        evidence: preuve,
      };
    }
    // 9.AM.1 — le document est MUET, et on peut le dire.
    //
    // La preuve négative a déjà été cherchée juste au-dessus : si elle existait, on
    // ne serait pas ici. Reste le cas où toutes les lectures ont rapporté ce champ
    // vide — le modèle a regardé, la proposition ne dit rien.
    //
    // L'ordre compte : `not_offered` prime toujours. Une garantie explicitement
    // exclue n'est pas une information manquante, et l'inverse serait la confusion
    // la plus coûteuse de ce tableau.
    if (input.reportedEmptyByAll === true) {
      return {
        cell_status: "not_provided",
        status_reason: "reported_empty_by_all",
        evidence: null,
      };
    }
    return { cell_status: "not_detected", status_reason: "no_value_no_evidence", evidence: null };
  }

  // 3. La cellule porte elle-même la mention négative → c'est un `not_offered`,
  //    pas une « valeur ». Afficher « NON OFFERTE » comme une valeur ordinaire
  //    obligerait le conseiller à interpréter du texte.
  if (isNegativeMention(input.value)) {
    const preuve = findNegativeEvidence(input);
    if (preuve) {
      return {
        cell_status: "not_offered",
        status_reason: "explicit_negative_mention",
        evidence: preuve,
      };
    }
  }

  // 4. Échelle ordonnée non reconnue → vérification, valeur conservée en texte.
  if (input.fieldKey === HOSPITAL_ROOM_FIELD && hospitalRoomLevel(input.value) === null) {
    return {
      cell_status: "needs_review",
      status_reason: "unrecognized_categorical_value",
      evidence: null,
    };
  }

  // 5. Doute réel : sur une cellule QUI PORTE une valeur, le `needs_review` de la
  //    consolidation est informatif (confiance basse, citation absente, désaccord).
  //    C'est uniquement sur les cellules nulles qu'il était mécanique — et celles-là
  //    ne passent jamais ici.
  if (input.needsReview) {
    return {
      cell_status: "needs_review",
      status_reason: "low_confidence_or_missing_citation",
      evidence: null,
    };
  }

  return { cell_status: "value", status_reason: "usable_value", evidence: null };
}

// ---------------------------------------------------------------------------
// Chunk 9.AH.1 — l'état de RÉCEPTION d'un assureur
// ---------------------------------------------------------------------------
//
// Un niveau AU-DESSUS de `cell_status`, et il ne faut jamais les confondre :
//
//   réception  — a-t-on quelque chose de cet assureur ?
//   extraction — ce qu'on a est-il exploitable ?
//   cellule    — que dit ce champ précis ?
//
// « Aucune réponse reçue » n'est ni `not_detected` ni `not_offered`. Les deux
// derniers portent sur un document QU'ON A LU ; le premier dit qu'il n'y a rien à
// lire. Les mélanger ferait conclure qu'un assureur n'offre pas une garantie alors
// qu'il ne s'est simplement pas encore prononcé — l'erreur la plus coûteuse que ce
// tableau puisse commettre, parce qu'elle est indétectable à la lecture.

/**
 * Ce qu'on peut dire d'un assureur sollicité, du plus vide au plus exploitable.
 *
 * `awaiting` et `declined` n'ont **aucune** colonne dans la matrice : une colonne
 * vide se lit « aucune garantie offerte ».
 */
export const RECEPTION_STATES = [
  /** Sollicité, rien reçu. Aucune colonne. */
  "awaiting",
  /** A refusé de soumettre. Aucune colonne — et ce n'est pas un échec. */
  "declined",
  /** Le délai est clos sans réponse. Aucune colonne. */
  "expired",
  /** Reçu, extraction pas encore aboutie. Colonne présente, valeurs incomplètes. */
  "extracting",
  /** Reçu et extrait, mais une revue humaine est requise avant de s'y fier. */
  "needs_review",
  /** L'extraction a échoué : on a le document, on n'en a pas tiré les données. */
  "extraction_failed",
  /** Reçu, extrait, exploitable. */
  "ready",
] as const;
export type ReceptionState = (typeof RECEPTION_STATES)[number];

/** Les états qui n'autorisent AUCUNE colonne dans la matrice. */
export const RECEPTION_WITHOUT_COLUMN: readonly ReceptionState[] = [
  "awaiting",
  "declined",
  "expired",
];

/**
 * Qualifie un assureur sollicité.
 *
 * `recipientStatus` vient de `app.qrr_status`, `submissionStatus` de
 * `app.submission_status`. L'absence de proposition l'emporte sur tout : sans
 * document, il n'y a rien à qualifier plus finement.
 */
export function qualifyReception(args: {
  recipientStatus: string;
  submissionStatus: string | null;
  /** Current pipeline evidence, supplied by the API; absent for legacy callers. */
  extraction?: { running: boolean; failed: boolean; complete: boolean; needsReview: boolean };
}): ReceptionState {
  const { recipientStatus, submissionStatus } = args;

  // Aucune proposition rattachée — on lit le destinataire, et rien d'autre.
  if (submissionStatus === null) {
    if (recipientStatus === "declined") return "declined";
    if (recipientStatus === "expired") return "expired";
    // `responded` sans proposition rattachée est un état RÉEL : le conseiller a
    // consigné la réponse avant de verser le document (9.AG.1, `submission_id`
    // optionnel). On attend donc encore quelque chose à comparer.
    return "awaiting";
  }

  if (args.extraction) {
    const progress = args.extraction;
    if (progress.running) return "extracting";
    if (progress.failed) return "extraction_failed";
    // A completed run is not a human approval; unresolved issues remain visible.
    if (!progress.complete || progress.needsReview || submissionStatus === "flagged") {
      return "needs_review";
    }
    return "ready";
  }

  switch (submissionStatus) {
    case "ready":
    case "reviewed":
      return "ready";
    case "flagged":
      return "needs_review";
    // `pending` et `extracting` disent tous deux « le document est là, les données
    // ne le sont pas encore ». Les distinguer dans le tableau n'aiderait pas : le
    // conseiller ne peut rien faire de plus dans les deux cas.
    case "pending":
    case "extracting":
      return "extracting";
    case "extracted":
      return "ready";
    default:
      // Un statut inconnu n'est pas « prêt ». Le supposer exploitable ferait
      // comparer des valeurs dont on ignore l'aboutissement.
      return "needs_review";
  }
}
